<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { nowApiDateTime } from '@yuksalish/ui'
import { useOperatorApi } from '../composables/useOperatorApi'
import OperatorPickerModal from '../components/OperatorPickerModal.vue'
import { Eraser, Hand, ListChecks, Maximize2, SquareDashedMousePointer, ZoomIn, ZoomOut } from 'lucide-vue-next'
import type { MachineLayoutDetail, MachineLayoutNode, MachineLayoutSummary, MachineRunCloseReason, OperatorEmployee, OperatorOrder, OperatorStage, OrderVariantOption } from '../types/operator'

const { t } = useI18n()
const {
  listLayouts,
  getLayout,
  listProductionOrders,
  getOrder,
  listStages,
  listProductionEmployees,
  startMachineRun,
  changeoverMachineRun,
  closeMachineRun,
} = useOperatorApi()

const layouts = ref<MachineLayoutSummary[]>([])
const selectedLayoutCode = ref('')
const layout = ref<MachineLayoutDetail | null>(null)
const orders = ref<OperatorOrder[]>([])
const operators = ref<OperatorEmployee[]>([])
const variants = ref<OrderVariantOption[]>([])
const stages = ref<OperatorStage[]>([])
const selectedMachines = ref<number[]>([])
// A batch run loads the SAME order variant on every selected machine.
const selectedVariantId = ref(0)
const quantities = reactive<Record<number, number>>({})
const bulkQuantity = ref<number | null>(null)
// The same selection can either load a new colour/size or close the open runs.
const batchAction = ref<'assign' | 'close'>('assign')
const closeReason = ref<MachineRunCloseReason>('shift_end')
const showOperatorPicker = ref(false)
const operatorLabel = ref('')
const CLOSE_REASONS: MachineRunCloseReason[] = ['completed', 'model_change', 'breakdown', 'shift_end', 'manual', 'order_cancelled']
const closeReasonOptions = computed<Record<string, string>>(() =>
  Object.fromEntries(CLOSE_REASONS.map((code) => [code, t(`operator.run.closeReasons.${code}`)])),
)
const loading = ref(true)
const loadingOrder = ref(false)
const submitting = ref(false)
const error = ref('')
const message = ref('')
const activeTab = ref<'machines' | 'matrix'>('machines')
const floorViewport = ref<HTMLDivElement | null>(null)
const floorMap = ref<HTMLDivElement | null>(null)
const mapScale = ref(1)
const mapX = ref(0)
const mapY = ref(0)
const mapMode = ref<'pan' | 'select'>('pan')
// Minimum on-screen height of a machine card, and the zoom ceiling for `Fit`.
const MIN_CELL_HEIGHT_PX = 40
const MAX_FIT_SCALE = 1.6
const marquee = ref<{ left: number; top: number; width: number; height: number } | null>(null)
let panPointerId: number | null = null
let pointerMode: 'pan' | 'marquee' | null = null
let panStartX = 0
let panStartY = 0
let panOriginX = 0
let panOriginY = 0
let marqueeAdditive = false
let mapWasDragged = false
const form = reactive({ order_id: 0, operator_employee_id: 0, stage_code: '', target_rate_per_hour: '', shift: 'day' })

const machineNodes = computed(() =>
  (layout.value?.nodes ?? []).filter(
    (node): node is MachineLayoutNode & { machine_id: number } => node.category === 'machine' && Boolean(node.machine_id),
  ),
)
const backgroundNodes = computed(() => (layout.value?.nodes ?? []).filter((node) => node.category !== 'machine'))
const batchVariantLabel = computed(() => {
  const variant = variants.value.find((item) => item.id === selectedVariantId.value)
  return variant ? `${variant.color_name} · ${variant.size_label}` : '—'
})

function point(node: MachineLayoutNode): { x: number; y: number } {
  const [x = 0, y = 0] = (node.loc || '').split(/\s+/).map(Number)
  return { x: Number.isFinite(x) ? x : 0, y: Number.isFinite(y) ? y : 0 }
}

function size(node: MachineLayoutNode): { width: number; height: number } {
  const [width = 60, height = 40] = (node.size || '').split(/\s+/).map(Number)
  return { width: width > 0 ? width : 60, height: height > 0 ? height : 40 }
}

const PREFERRED_CELL_WIDTH = 150
const PREFERRED_CELL_HEIGHT = 54

/**
 * Cards are drawn wider than the stored go.js node so the code and the
 * colour/size lines are readable, capped by the smallest gap between machine
 * centres so neighbouring cards never overlap.
 */
const cellMetrics = computed(() => {
  const nodes = machineNodes.value
  if (!nodes.length) return { width: PREFERRED_CELL_WIDTH, height: PREFERRED_CELL_HEIGHT }
  const smallestGap = (values: number[], fallback: number): number => {
    const sorted = Array.from(new Set(values)).sort((a, b) => a - b)
    const gaps = sorted.slice(1).map((value, index) => value - sorted[index]).filter((value) => value > 0)
    return gaps.length ? Math.min(...gaps) : fallback
  }
  const gapX = smallestGap(nodes.map((node) => point(node).x), PREFERRED_CELL_WIDTH / 0.92)
  const gapY = smallestGap(nodes.map((node) => point(node).y), PREFERRED_CELL_HEIGHT / 0.9)
  return {
    width: Math.min(PREFERRED_CELL_WIDTH, gapX * 0.92),
    height: Math.min(PREFERRED_CELL_HEIGHT, gapY * 0.9),
  }
})

/** Size actually rendered for a machine card (never smaller than the stored node). */
function cellSize(node: MachineLayoutNode): { width: number; height: number } {
  const stored = size(node)
  return {
    width: Math.max(stored.width, cellMetrics.value.width),
    height: Math.max(stored.height, cellMetrics.value.height),
  }
}

/** `loc` is the go.js node centre, so the CSS box is offset by half the size. */
function nodeStyle(node: MachineLayoutNode): Record<string, string | number> {
  const { x, y } = point(node)
  const { width, height } = cellSize(node)
  const origin = contentBox.value ?? { x: 0, y: 0 }
  return {
    left: `${x - width / 2 - origin.x}px`,
    top: `${y - height / 2 - origin.y}px`,
    width: `${width}px`,
    height: `${height}px`,
    zIndex: node.zOrder || 1,
    transform: `rotate(${node.angle || 0}deg)`,
  }
}

/**
 * Bounding box of every node on the layout. Fitting to the canvas size would
 * leave machines stranded in one corner of a large, mostly empty floor plan,
 * so the map scales to the drawn content instead.
 */
const contentBox = computed(() => {
  const nodes = layout.value?.nodes ?? []
  if (!nodes.length) return null
  let minX = Number.POSITIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY
  for (const node of nodes) {
    const { x, y } = point(node)
    const { width, height } = cellSize(node)
    minX = Math.min(minX, x - width / 2)
    minY = Math.min(minY, y - height / 2)
    maxX = Math.max(maxX, x + width / 2)
    maxY = Math.max(maxY, y + height / 2)
  }
  const pad = 44
  return {
    x: minX - pad,
    y: minY - pad,
    width: Math.max(maxX - minX + pad * 2, 1),
    height: Math.max(maxY - minY + pad * 2, 1),
  }
})

/** Machines in these states cannot take a NEW run (they can still be closed). */
function blockedState(node: MachineLayoutNode): boolean {
  return ['down', 'blocked', 'maintenance', 'off'].includes(node.state ?? '')
}

function hasActiveRun(node: MachineLayoutNode): boolean {
  return Boolean(node.run_id || node.order_id)
}

function machineClass(node: MachineLayoutNode): string {
  if (['down', 'blocked'].includes(node.state ?? '')) return 'is-danger'
  if (node.state === 'maintenance') return 'is-maintenance'
  if (node.run_id || node.order_id) return 'is-active'
  return 'is-empty'
}

/** Font sizes in layout px so the code, colour and size lines fit the cell. */
function cellFont(node: MachineLayoutNode): { code: number; line: number } {
  const { width, height } = cellSize(node)
  const longest = Math.max(4, (node.machine_code || '').length)
  const byHeight = Math.floor(height / 4)
  const byWidth = Math.floor(width / (longest * 0.62))
  const code = Math.max(6, Math.min(16, Math.min(byHeight, byWidth)))
  return { code, line: Math.max(5, Math.round(code * 0.85)) }
}

function cellCodeStyle(node: MachineLayoutNode): Record<string, string> {
  return { fontSize: `${cellFont(node).code}px` }
}

function cellLineStyle(node: MachineLayoutNode): Record<string, string> {
  return { fontSize: `${cellFont(node).line}px` }
}

function pickOperator(employee: OperatorEmployee): void {
  form.operator_employee_id = employee.id
  operatorLabel.value = `${employee.employee_code} · ${employee.first_name} ${employee.last_name}`.trim()
}

function clearSelections(): void {
  activeTab.value = 'machines'
  selectedMachines.value = []
  selectedVariantId.value = 0
  bulkQuantity.value = null
  Object.keys(quantities).forEach((key) => delete quantities[Number(key)])
}

function openMatrixTab(): void {
  error.value = ''
  if (!selectedMachines.value.length) {
    error.value = t('operator.shopfloor.selectMachineFirst')
    return
  }
  activeTab.value = 'matrix'
}

async function selectLayout(code: string): Promise<void> {
  selectedLayoutCode.value = code
  clearSelections()
  error.value = ''
  try {
    layout.value = await getLayout(code)
  } catch (cause) {
    layout.value = null
    error.value = formatApiError(cause)
    return
  }
  mapScale.value = 1
  mapX.value = 0
  mapY.value = 0
  await fitMap()
}

function toggleMachine(node: MachineLayoutNode & { machine_id: number }): void {
  if (mapWasDragged) return
  const selected = selectedMachines.value.includes(node.machine_id)
  applySelection(
    selected
      ? selectedMachines.value.filter((id) => id !== node.machine_id)
      : [...selectedMachines.value, node.machine_id],
    false,
  )
}

/** Applies a machine selection, dropping quantities of de-selected machines. */
function applySelection(machineIds: number[], additive: boolean): void {
  const next = additive
    ? Array.from(new Set([...selectedMachines.value, ...machineIds]))
    : Array.from(new Set(machineIds))
  selectedMachines.value = next
  for (const key of Object.keys(quantities)) {
    if (!next.includes(Number(key))) delete quantities[Number(key)]
  }
}

function selectAllMachines(): void {
  applySelection(
    machineNodes.value.map((node) => node.machine_id),
    false,
  )
}

function clearMachineSelection(): void {
  applySelection([], false)
}

/** Machine ids whose centre falls inside the rubber-band rectangle (viewport px). */
function machinesInRect(rect: { left: number; top: number; width: number; height: number }): number[] {
  const scale = mapScale.value || 1
  const left = (rect.left - mapX.value) / scale
  const top = (rect.top - mapY.value) / scale
  const right = (rect.left + rect.width - mapX.value) / scale
  const bottom = (rect.top + rect.height - mapY.value) / scale
  return machineNodes.value
    .filter((node) => {
      const { x, y } = point(node)
      return x >= left && x <= right && y >= top && y <= bottom
    })
    .map((node) => node.machine_id)
}

function clampScale(value: number): number {
  return Math.min(3, Math.max(0.05, value))
}

function zoomMap(nextScale: number, clientX?: number, clientY?: number): void {
  const viewport = floorViewport.value
  if (!viewport) return
  const scale = clampScale(nextScale)
  const rect = viewport.getBoundingClientRect()
  const focusX = (clientX ?? rect.left + rect.width / 2) - rect.left
  const focusY = (clientY ?? rect.top + rect.height / 2) - rect.top
  const ratio = scale / mapScale.value
  mapX.value = focusX - (focusX - mapX.value) * ratio
  mapY.value = focusY - (focusY - mapY.value) * ratio
  mapScale.value = scale
}

async function fitMap(): Promise<void> {
  await nextTick()
  const viewport = floorViewport.value
  const box = contentBox.value
  if (!viewport || !box) return
  const fitScale = Math.min(viewport.clientWidth / box.width, viewport.clientHeight / box.height) * 0.96
  // Never shrink the cards below a readable size: a wide floor plan is panned
  // instead of being squeezed into unreadable slivers.
  const smallest = machineNodes.value.length
    ? Math.min(...machineNodes.value.map((node) => size(node).height))
    : 0
  const readableScale = smallest > 0 ? MIN_CELL_HEIGHT_PX / smallest : fitScale
  // Zoom in for legible cards, but never crop more than a narrow margin.
  const scale = clampScale(
    Math.min(MAX_FIT_SCALE, Math.max(fitScale, Math.min(readableScale, fitScale * 1.15))),
  )
  mapScale.value = scale
  mapX.value = (viewport.clientWidth - box.width * scale) / 2
  mapY.value = (viewport.clientHeight - box.height * scale) / 2
}

function onPointerDown(event: PointerEvent): void {
  // A press on a machine card is a selection click, not a map gesture.
  if ((event.target as HTMLElement | null)?.closest('.floor-machine')) return
  if (event.button !== 0 && event.button !== 1) return
  panPointerId = event.pointerId
  panStartX = event.clientX
  panStartY = event.clientY
  panOriginX = mapX.value
  panOriginY = mapY.value
  mapWasDragged = false
  marqueeAdditive = event.shiftKey || event.ctrlKey || event.metaKey
  pointerMode = event.button === 0 && mapMode.value === 'select' ? 'marquee' : 'pan'
  floorViewport.value?.setPointerCapture(event.pointerId)
  if (pointerMode === 'marquee') updateMarquee(event)
}

function onPointerMove(event: PointerEvent): void {
  if (panPointerId !== event.pointerId || !pointerMode) return
  if (Math.abs(event.clientX - panStartX) + Math.abs(event.clientY - panStartY) > 5) {
    mapWasDragged = true
  }
  if (pointerMode === 'pan') {
    mapX.value = panOriginX + (event.clientX - panStartX)
    mapY.value = panOriginY + (event.clientY - panStartY)
    return
  }
  updateMarquee(event)
}

function updateMarquee(event: PointerEvent): void {
  const rect = floorViewport.value?.getBoundingClientRect()
  if (!rect) return
  const startX = panStartX - rect.left
  const startY = panStartY - rect.top
  const x = event.clientX - rect.left
  const y = event.clientY - rect.top
  marquee.value = {
    left: Math.min(startX, x),
    top: Math.min(startY, y),
    width: Math.abs(x - startX),
    height: Math.abs(y - startY),
  }
}

function onPointerUp(event: PointerEvent): void {
  if (panPointerId !== event.pointerId) return
  if (pointerMode === 'marquee' && marquee.value) {
    const dragged = marquee.value.width > 4 || marquee.value.height > 4
    const hits = machinesInRect(marquee.value)
    if (dragged) applySelection(hits, marqueeAdditive)
    else if (!marqueeAdditive) clearMachineSelection()
  }
  marquee.value = null
  floorViewport.value?.releasePointerCapture(event.pointerId)
  panPointerId = null
  pointerMode = null
  window.setTimeout(() => { mapWasDragged = false }, 0)
}

function wheelZoom(event: WheelEvent): void {
  zoomMap(mapScale.value * (event.deltaY < 0 ? 1.12 : 0.89), event.clientX, event.clientY)
}

async function selectOrder(): Promise<void> {
  variants.value = []
  stages.value = []
  form.stage_code = ''
  clearSelections()
  if (!form.order_id) return
  loadingOrder.value = true
  try {
    const [order, orderStages] = await Promise.all([getOrder(form.order_id), listStages(form.order_id)])
    variants.value = order.variants ?? []
    stages.value = orderStages
    form.stage_code = orderStages[0]?.stage_code ?? ''
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loadingOrder.value = false
  }
}

/** One colour/size for the whole batch: it is applied to every selected machine. */
function pickBatchVariant(variant: OrderVariantOption): void {
  selectedVariantId.value = variant.id
  const planned = Number(variant.planned_quantity) || 1
  for (const machineId of selectedMachines.value) {
    if (!quantities[machineId]) quantities[machineId] = planned
  }
}

function applyBulkQuantity(): void {
  const value = Number(bulkQuantity.value)
  if (!Number.isFinite(value) || value <= 0) return
  for (const machineId of selectedMachines.value) quantities[machineId] = value
}

function nodeOf(machineId: number): MachineLayoutNode | undefined {
  return machineNodes.value.find((node) => node.machine_id === machineId)
}

function summarise(results: PromiseSettledResult<unknown>[], successKey: string, skipNote = ''): void {
  const failures = results.filter((result) => result.status === 'rejected')
  const success = results.length - failures.length
  if (failures.length) {
    error.value = `${t('operator.shopfloor.batchPartial', { success, failed: failures.length })} ${formatApiError((failures[0] as PromiseRejectedResult).reason)}`
  } else {
    message.value = `${t(successKey, { count: success })}${skipNote}`
  }
}

async function submit(): Promise<void> {
  error.value = ''
  message.value = ''
  if (!selectedMachines.value.length) {
    error.value = t('operator.shopfloor.selectMachineFirst')
    return
  }
  submitting.value = true
  if (batchAction.value === 'close') await submitClose()
  else await submitAssign()
  submitting.value = false
  if (selectedLayoutCode.value) await selectLayout(selectedLayoutCode.value)
}

/** Batch-close the open run of every selected machine that currently runs. */
async function submitClose(): Promise<void> {
  const targets = selectedMachines.value
    .map((machineId) => ({ machineId, runId: nodeOf(machineId)?.run_id ?? null }))
    .filter((item): item is { machineId: number; runId: number } => Boolean(item.runId))
  if (!targets.length) {
    error.value = t('operator.shopfloor.batchCloseNoRuns')
    return
  }
  const results = await Promise.allSettled(
    targets.map((target) =>
      closeMachineRun(target.machineId, target.runId, {
        ended_at: nowApiDateTime(),
        close_reason: closeReason.value,
      }),
    ),
  )
  const skipped = selectedMachines.value.length - targets.length
  summarise(results, 'operator.shopfloor.batchClosed', skipped > 0 ? ` ${t('operator.shopfloor.batchSkipped', { count: skipped })}` : '')
}

/** Load the same colour/size on every selected machine (changeover when it runs). */
async function submitAssign(): Promise<void> {
  if (!form.order_id || !form.operator_employee_id || !form.stage_code) {
    error.value = t('operator.shopfloor.batchRequired')
    return
  }
  const variant = variants.value.find((item) => item.id === selectedVariantId.value)
  if (!variant) {
    error.value = t('operator.shopfloor.batchVariantRequired')
    return
  }
  const ready = selectedMachines.value.filter((machineId) => {
    const node = nodeOf(machineId)
    return node ? !blockedState(node) : false
  })
  if (!ready.length) {
    error.value = t('operator.shopfloor.batchNoAssignable')
    return
  }
  const payload = (machineId: number) => ({
    order_id: form.order_id,
    variant_id: variant.id,
    color_name: variant.color_name,
    size_label: variant.size_label,
    stage_code: form.stage_code,
    planned_quantity: Number(quantities[machineId]) || Number(variant.planned_quantity) || 1,
    target_rate_per_hour: Number(form.target_rate_per_hour) || undefined,
    operator_employee_id: form.operator_employee_id,
    shift: form.shift,
    started_at: nowApiDateTime(),
  })
  const results = await Promise.allSettled(
    ready.map((machineId) => {
      const runId = nodeOf(machineId)?.run_id
      if (runId) {
        return changeoverMachineRun(machineId, runId, { ...payload(machineId), changeover_started_at: nowApiDateTime(), close_reason: 'model_change' })
      }
      return startMachineRun(machineId, payload(machineId))
    }),
  )
  const skipped = selectedMachines.value.length - ready.length
  summarise(results, 'operator.shopfloor.batchAssigned', skipped > 0 ? ` ${t('operator.shopfloor.batchSkipped', { count: skipped })}` : '')
}

async function load(): Promise<void> {
  loading.value = true
  error.value = ''
  try {
    const [layoutRows, active, planned, employeeRows] = await Promise.all([
      listLayouts(),
      listProductionOrders({ status: 'in_progress', page: 1, per_page: 100 }),
      listProductionOrders({ status: 'planned', page: 1, per_page: 100 }),
      listProductionEmployees(),
    ])
    layouts.value = layoutRows
    operators.value = employeeRows
    const seen = new Set<number>()
    orders.value = [...active.items, ...planned.items].filter((order) => !seen.has(order.id) && Boolean(seen.add(order.id)))
    if (layoutRows[0]) await selectLayout(layoutRows[0].code)
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
  await fitMap()
}

onMounted(load)

// Keep the fitted view correct when the window or the tool rail changes size.
let resizeFrame = 0
const resizeObserver =
  typeof ResizeObserver === 'undefined'
    ? null
    : new ResizeObserver(() => {
        window.cancelAnimationFrame(resizeFrame)
        resizeFrame = window.requestAnimationFrame(() => {
          void fitMap()
        })
      })

watch(floorViewport, (element) => {
  resizeObserver?.disconnect()
  if (element) resizeObserver?.observe(element)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  window.cancelAnimationFrame(resizeFrame)
})
</script>

<template>
  <div class="page-heading">
    <div><p class="eyebrow">{{ t('operator.shopfloor.batchEyebrow') }}</p><h2>{{ t('operator.shopfloor.batchAssign') }}</h2><p>{{ t('operator.shopfloor.batchPageDescription') }}</p></div>
    <RouterLink class="btn-secondary" to="/shopfloor">{{ t('operator.shopfloor.backToShopfloor') }}</RouterLink>
  </div>
  <p v-if="message" class="notice success">{{ message }}</p>
  <p v-if="error" class="notice error">{{ error }}</p>
  <div v-if="loading" class="state">{{ t('operator.state.loading') }}</div>
  <form v-else class="assignment-page" @submit.prevent="submit">
    <section class="settings-card">
      <div class="batch-actions" role="group" :aria-label="t('operator.shopfloor.batchActionLabel')">
        <button type="button" :class="{ active: batchAction === 'assign' }" @click="batchAction = 'assign'; activeTab = 'machines'">
          {{ t('operator.shopfloor.batchActionAssign') }}
        </button>
        <button type="button" :class="{ active: batchAction === 'close' }" @click="batchAction = 'close'; activeTab = 'machines'">
          {{ t('operator.shopfloor.batchActionClose') }}
        </button>
      </div>
      <div v-if="batchAction === 'assign'" class="form-grid">
        <label>{{ t('operator.run.order') }}<select v-model.number="form.order_id" @change="selectOrder"><option :value="0" disabled>{{ t('operator.run.selectOrder') }}</option><option v-for="order in orders" :key="order.id" :value="order.id">{{ order.mo_code }} · {{ order.product_name }}</option></select></label>
        <label>{{ t('operator.shopfloor.operatorWorker') }}<button class="picker-trigger" type="button" @click="showOperatorPicker = true">{{ operatorLabel || t('operator.shopfloor.selectOperator') }}</button></label>
        <label>{{ t('operator.run.stage') }}<select v-model="form.stage_code" :disabled="!stages.length"><option value="" disabled>{{ t('operator.run.selectStage') }}</option><option v-for="stage in stages" :key="stage.stage_code" :value="stage.stage_code">{{ stage.stage_name }}</option></select></label>
        <label>{{ t('operator.run.shift') }}<select v-model="form.shift"><option value="day">{{ t('operator.run.day') }}</option><option value="night">{{ t('operator.run.night') }}</option></select></label>
        <label>{{ t('operator.run.targetRate') }}<input v-model="form.target_rate_per_hour" type="number" min="0" /></label>
      </div>
      <div v-else class="form-grid">
        <label>{{ t('operator.run.closeReason') }}<select v-model="closeReason"><option v-for="(label, code) in closeReasonOptions" :key="code" :value="code">{{ label }}</option></select></label>
      </div>
      <p class="map-hint">{{ batchAction === 'close' ? t('operator.shopfloor.batchCloseHint') : t('operator.shopfloor.batchAssignHint') }}</p>
    </section>

    <div class="workspace">
    <nav class="workspace-tabs" :aria-label="t('operator.shopfloor.assignmentSteps')">
      <button type="button" :class="{ active: activeTab === 'machines' }" @click="activeTab = 'machines'">
        <span>1</span>{{ t('operator.shopfloor.pickMachinesTab') }}
      </button>
      <button type="button" :class="{ active: activeTab === 'matrix' }" :disabled="!selectedMachines.length || batchAction !== 'assign'" @click="openMatrixTab">
        <span>2</span>{{ t('operator.shopfloor.assignMatrixTab') }}
      </button>
    </nav>

    <section v-if="activeTab === 'machines'" class="map-card">
      <div class="section-head">
        <div><h3>{{ t('operator.shopfloor.selectMachines') }}</h3><span>{{ t('operator.shopfloor.selectedCount', { count: selectedMachines.length }) }}</span></div>
        <div class="map-tools">
          <select :value="selectedLayoutCode" @change="selectLayout(($event.target as HTMLSelectElement).value)"><option v-for="item in layouts" :key="item.code" :value="item.code">{{ item.name }}</option></select>
          <div class="mode-tools" role="group" :aria-label="t('operator.shopfloor.mapMode')">
            <button type="button" :class="{ active: mapMode === 'pan' }" :title="t('operator.shopfloor.mapPan')" :aria-label="t('operator.shopfloor.mapPan')" :aria-pressed="mapMode === 'pan'" @click="mapMode = 'pan'">
              <Hand :size="16" aria-hidden="true" />
            </button>
            <button type="button" :class="{ active: mapMode === 'select' }" :title="t('operator.shopfloor.mapSelect')" :aria-label="t('operator.shopfloor.mapSelect')" :aria-pressed="mapMode === 'select'" @click="mapMode = 'select'">
              <SquareDashedMousePointer :size="16" aria-hidden="true" />
            </button>
          </div>
          <div class="selection-tools" role="group" :aria-label="t('operator.shopfloor.selectAll')">
            <button type="button" :title="t('operator.shopfloor.selectAll')" :aria-label="t('operator.shopfloor.selectAll')" @click="selectAllMachines">
              <ListChecks :size="16" aria-hidden="true" />
            </button>
            <button type="button" :disabled="!selectedMachines.length" :title="t('operator.shopfloor.clearSelection')" :aria-label="t('operator.shopfloor.clearSelection')" @click="clearMachineSelection">
              <Eraser :size="16" aria-hidden="true" />
            </button>
          </div>
          <div class="zoom-tools" role="group" :aria-label="t('operator.shopfloor.zoomControls')">
            <button type="button" :title="t('operator.shopfloor.zoomOut')" :aria-label="t('operator.shopfloor.zoomOut')" @click="zoomMap(mapScale / 1.25)">
              <ZoomOut :size="16" aria-hidden="true" />
            </button>
            <button type="button" :title="t('operator.shopfloor.zoomFit')" :aria-label="t('operator.shopfloor.zoomFit')" @click="fitMap">
              <Maximize2 :size="16" aria-hidden="true" />
            </button>
            <button type="button" :title="t('operator.shopfloor.zoomIn')" :aria-label="t('operator.shopfloor.zoomIn')" @click="zoomMap(mapScale * 1.25)">
              <ZoomIn :size="16" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <p v-if="layout && machineNodes.length" class="map-hint">{{ mapMode === 'select' ? t('operator.shopfloor.marqueeHint') : t('operator.shopfloor.panHint') }}</p>
      <p v-if="layout && !machineNodes.length" class="matrix-empty">{{ t('operator.shopfloor.noMachinesInLayout') }}</p>
      <div
        v-else-if="layout"
        ref="floorViewport"
        class="floor-scroll"
        :class="{ 'is-select': mapMode === 'select' }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
        @wheel.prevent="wheelZoom"
      >
        <div
          ref="floorMap"
          class="floor-map"
          :style="{
            width: `${contentBox?.width ?? layout.layout.width ?? 1}px`,
            height: `${contentBox?.height ?? layout.layout.height ?? 1}px`,
            transform: `translate(${mapX}px, ${mapY}px) scale(${mapScale})`,
          }"
        >
          <div v-for="node in backgroundNodes" :key="node.key" class="floor-zone" :style="nodeStyle(node)">{{ node.label }}</div>
          <button v-for="node in machineNodes" :key="node.key" type="button" class="floor-machine" :class="[machineClass(node), { selected: selectedMachines.includes(node.machine_id) }]" :style="nodeStyle(node)" @click="toggleMachine(node)"><strong class="cell-code" :style="cellCodeStyle(node)">{{ node.machine_code }}</strong><span class="cell-line" :style="cellLineStyle(node)">{{ node.color_name || '—' }}</span><span class="cell-line" :style="cellLineStyle(node)">{{ node.size_label || '—' }}</span></button>
        </div>
        <div
          v-if="marquee"
          class="marquee-box"
          :style="{ left: `${marquee.left}px`, top: `${marquee.top}px`, width: `${marquee.width}px`, height: `${marquee.height}px` }"
        ></div>
      </div>
    </section>

    <section v-if="activeTab === 'matrix'" class="matrix-card">
      <h3>{{ t('operator.shopfloor.batchVariantTitle') }}</h3>
      <p v-if="!variants.length" class="matrix-empty">{{ t('operator.shopfloor.selectOrderForMatrix') }}</p>
      <template v-else>
        <p class="map-hint">{{ t('operator.shopfloor.batchVariantHint', { count: selectedMachines.length }) }}</p>
        <div class="variant-picker" role="radiogroup" :aria-label="t('operator.shopfloor.batchVariantTitle')">
          <button
            v-for="variant in variants"
            :key="variant.id"
            type="button"
            class="variant-option"
            :class="{ selected: selectedVariantId === variant.id }"
            role="radio"
            :aria-checked="selectedVariantId === variant.id"
            @click="pickBatchVariant(variant)"
          >
            <strong>{{ variant.color_name }}</strong>
            <small>{{ variant.size_label }}</small>
            <span>{{ t('operator.run.plannedQuantity') }}: {{ variant.planned_quantity }}</span>
          </button>
        </div>

        <div class="bulk-quantity">
          <label>{{ t('operator.shopfloor.quantityForAll') }}<input v-model.number="bulkQuantity" type="number" min="1" /></label>
          <button class="btn-secondary" type="button" :disabled="!bulkQuantity || bulkQuantity <= 0" @click="applyBulkQuantity">
            {{ t('operator.shopfloor.applyToAll') }}
          </button>
        </div>

        <div class="matrix-scroll"><table><thead><tr><th>{{ t('operator.shopfloor.machine') }}</th><th>{{ t('operator.shopfloor.variantColumn') }}</th><th>{{ t('operator.run.plannedQuantity') }}</th></tr></thead><tbody><tr v-for="machineId in selectedMachines" :key="machineId"><th>{{ machineNodes.find((node) => node.machine_id === machineId)?.machine_code }}</th><td>{{ batchVariantLabel }}</td><td><input v-model.number="quantities[machineId]" class="quantity" type="number" min="1"></td></tr></tbody></table></div>
      </template>
    </section>
    </div>
    <div class="actions">
      <button v-if="activeTab === 'machines' && batchAction === 'assign'" class="btn-primary" type="button" :disabled="!selectedMachines.length" @click="openMatrixTab">{{ t('operator.shopfloor.continueToMatrix') }}</button>
      <button v-else class="btn-primary" type="submit" :disabled="submitting || !selectedMachines.length">
        {{ submitting ? t('operator.run.submitting') : batchAction === 'close' ? t('operator.shopfloor.batchCloseSelected') : t('operator.shopfloor.assignSelected') }}
      </button>
    </div>
  </form>

  <OperatorPickerModal
    :open="showOperatorPicker"
    :employees="operators"
    :selected-id="form.operator_employee_id"
    @close="showOperatorPicker = false"
    @select="pickOperator"
  />
</template>

<style scoped>
.page-heading{display:flex;justify-content:space-between;gap:16px;margin-bottom:18px}.eyebrow{margin:0 0 4px;color:#65736e;font-size:12px;font-weight:700;text-transform:uppercase}h2{margin:0 0 4px;font-size:22px}.page-heading p{margin:0;color:#5f6b6a}.notice{padding:10px 12px;border-radius:8px;font-size:13px;font-weight:700}.notice.success{background:#e8f7ef;color:#176344}.notice.error{background:#fff0ef;color:#a63232}.state{padding:50px;text-align:center}.assignment-page{display:grid;gap:16px}.settings-card,.map-card,.matrix-card{padding:16px;border:1px solid #dce5e1;border-radius:12px;background:#fff}.form-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(180px,1fr));gap:12px}.form-grid label{display:grid;gap:5px;color:#45564f;font-size:12px;font-weight:700}.batch-actions{display:inline-flex;overflow:hidden;margin-bottom:12px;border:1px solid #cbd6d2;border-radius:8px}.batch-actions button{padding:8px 14px;border:0;border-right:1px solid #dce5e1;background:#fff;color:#45564f;font-size:13px;font-weight:700;cursor:pointer}.batch-actions button:last-child{border-right:0}.batch-actions button.active{background:#16869a;color:#fff}.picker-trigger{padding:9px;border:1px solid #cbd6d2;border-radius:7px;background:#fff;color:#22342e;font:inherit;text-align:left;cursor:pointer}.picker-trigger:hover{border-color:#16869a}.form-grid select,.form-grid input,.section-head select{padding:9px;border:1px solid #cbd6d2;border-radius:7px;background:#fff}.section-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}.section-head h3,.matrix-card h3{margin:0 0 3px}.section-head span{color:#65736e;font-size:12px}.floor-scroll{max-height:65vh;overflow:auto;border-radius:10px;background:#071521}.floor-map{position:relative;overflow:hidden;background-color:#071521;background-image:linear-gradient(rgb(64 104 128 / 20%) 1px,transparent 1px),linear-gradient(90deg,rgb(64 104 128 / 20%) 1px,transparent 1px);background-size:20px 20px}.floor-zone,.floor-machine{position:absolute;box-sizing:border-box}.floor-zone{display:grid;place-items:center;border:1px solid #315064;background:#102635;color:#a9c3d2;font-size:10px}.floor-machine{display:flex;min-width:30px;min-height:24px;flex-direction:column;align-items:center;justify-content:center;gap:0;overflow:hidden;padding:1px 2px;border:1px solid #2f5b4e;border-radius:5px;color:#172c25;line-height:1.1;cursor:pointer}.floor-machine .cell-code{max-width:100%;overflow:hidden;font-weight:700;text-overflow:ellipsis;white-space:nowrap}.floor-machine .cell-line{max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.floor-machine.is-empty{background:#ecefed}.floor-machine.is-active{background:#c9edd8;border-color:#2f7d55}.floor-machine.is-danger{background:#f8d7da}.floor-machine.is-maintenance{background:#dce6f8}.floor-machine.selected{outline:4px solid #f2892b;outline-offset:2px;box-shadow:0 0 0 6px rgb(242 137 43 / 28%)}.floor-machine.unavailable{cursor:not-allowed;filter:saturate(.4);opacity:.55}.matrix-scroll{overflow:auto;border:1px solid #dce5e1;border-radius:9px}.matrix-scroll table{width:100%;min-width:620px;border-collapse:collapse;font-size:12px}.matrix-scroll th,.matrix-scroll td{padding:8px;border-right:1px solid #e3e9e6;border-bottom:1px solid #e3e9e6;text-align:center}.matrix-scroll thead th{background:#f1f6f4}.matrix-choice{display:grid;width:30px;height:30px;margin:auto;place-items:center;border:1px solid #bfcac6;border-radius:7px;color:transparent;cursor:pointer}.matrix-choice input{position:absolute;opacity:0}.matrix-choice.selected{border-color:#16869a;background:#16869a;color:#fff}.quantity{width:88px;padding:6px;border:1px solid #cbd6d2;border-radius:6px}.actions{display:flex;justify-content:flex-end;position:sticky;bottom:0;padding:12px 0;background:#f7faf9}@media(max-width:700px){.page-heading{flex-direction:column}}
/* Desktop planner workspace: controls stay in a narrow tool rail while the
   coordinate map receives the full remaining viewport. */
@media (min-width: 961px) {
  .assignment-page {
    grid-template-columns: 300px minmax(0, 1fr);
    grid-template-rows: auto auto;
    align-items: start;
  }
  .settings-card {
    position: sticky;
    top: 12px;
    grid-column: 1;
    grid-row: 1;
    max-height: calc(100vh - 190px);
    overflow-y: auto;
  }
  .form-grid { grid-template-columns: 1fr; }
  .actions {
    position: sticky;
    top: calc(100vh - 92px);
    bottom: auto;
    z-index: 3;
    grid-column: 1;
    grid-row: 2;
    padding: 12px;
    border: 1px solid #dce5e1;
    border-radius: 10px;
    background: #fff;
  }
  .actions .btn-primary {
    width: 100%;
    min-height: 52px;
    padding: 14px 18px;
    border-radius: 10px;
    font-size: 15px;
    letter-spacing: .01em;
  }
  .workspace {
    grid-column: 2;
    grid-row: 1 / span 2;
    display: grid;
    gap: 16px;
    min-width: 0;
    align-content: start;
  }
  .map-card, .matrix-card {
    grid-column: auto;
    grid-row: auto;
    min-width: 0;
    padding: 12px;
  }
  .floor-scroll {
    height: clamp(360px, calc(100vh - 235px), 760px);
    max-height: none;
    min-height: 0;
  }
}

@media (max-width: 960px) {
  .assignment-page { grid-template-columns: 1fr; }
  .workspace { display: grid; grid-column: 1; gap: 16px; min-width: 0; }
  .settings-card, .map-card, .matrix-card, .actions { grid-column: 1; }
  .floor-scroll { height: 62vh; max-height: 62vh; }
  .actions .btn-primary { width: 100%; min-height: 50px; padding: 13px 18px; border-radius: 10px; font-size: 15px; }
}
.workspace-tabs { display: flex; gap: 6px; padding: 5px; border: 1px solid #dce5e1; border-radius: 10px; background: #fff; }
.workspace-tabs button { display: flex; align-items: center; gap: 7px; padding: 9px 14px; border: 0; border-radius: 7px; background: transparent; color: #60716a; font-weight: 700; cursor: pointer; }
.workspace-tabs button span { display: grid; width: 22px; height: 22px; place-items: center; border-radius: 50%; background: #e7eeeb; font-size: 11px; }
.workspace-tabs button.active { background: #e5f5f7; color: #116f80; }
.workspace-tabs button.active span { background: #16869a; color: #fff; }
.workspace-tabs button:disabled { opacity: .45; cursor: not-allowed; }
.matrix-empty { margin: 30px 0; color: #65736e; text-align: center; }
.map-hint { margin: 0 0 8px; color: #65736e; font-size: 12px; }
.map-tools { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.mode-tools, .selection-tools { display: inline-flex; overflow: hidden; border: 1px solid #cbd6d2; border-radius: 7px; }
.mode-tools button, .selection-tools button { padding: 7px 11px; border: 0; border-right: 1px solid #dce5e1; background: #fff; color: #176d7d; font-size: 12px; font-weight: 700; cursor: pointer; }
.mode-tools button:last-child, .selection-tools button:last-child { border-right: 0; }
.selection-tools button { color: #4c615a; }
.mode-tools button:hover, .selection-tools button:hover:not(:disabled) { background: #edf7f6; }
.mode-tools button.active { background: #16869a; color: #fff; }
.selection-tools button:disabled { opacity: .5; cursor: not-allowed; }
.marquee-box { position: absolute; z-index: 30; border: 1px dashed #19b7d0; background: rgb(25 183 208 / 16%); pointer-events: none; }
.variant-picker { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 8px; margin-bottom: 12px; }
.variant-option { display: grid; gap: 2px; padding: 9px 11px; border: 1px solid #cbd6d2; border-radius: 9px; background: #fff; color: #2a3a35; text-align: left; cursor: pointer; }
.variant-option strong { font-size: 13px; }
.variant-option small { color: #65736e; font-size: 11px; }
.variant-option span { color: #7b8a85; font-size: 11px; }
.variant-option:hover { border-color: #16869a; }
.variant-option.selected { border-color: #16869a; background: #e5f5f7; box-shadow: inset 0 0 0 1px #16869a; }
.bulk-quantity { display: flex; align-items: flex-end; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.bulk-quantity label { display: grid; gap: 5px; color: #45564f; font-size: 12px; font-weight: 700; }
.bulk-quantity input { width: 120px; padding: 8px; border: 1px solid #cbd6d2; border-radius: 7px; }
.bulk-quantity .btn-secondary { padding: 8px 14px; border-radius: 8px; font-size: 12px; font-weight: 700; }
.zoom-tools { display: inline-flex; overflow: hidden; border: 1px solid #cbd6d2; border-radius: 7px; }
.zoom-tools button { min-width: 32px; padding: 7px 9px; border: 0; border-right: 1px solid #dce5e1; background: #fff; color: #176d7d; font-weight: 800; cursor: pointer; }
.zoom-tools button:last-child { border-right: 0; }
.zoom-tools button:hover { background: #edf7f6; }
.floor-scroll { position: relative; overflow: hidden; cursor: grab; touch-action: none; user-select: none; }
.floor-scroll:active { cursor: grabbing; }
.floor-scroll.is-select, .floor-scroll.is-select:active { cursor: crosshair; }
.floor-map { position: absolute; inset: 0 auto auto 0; transform-origin: 0 0; will-change: transform; }
.floor-machine { user-select: none; }
/* Icon-only map toolbox: square buttons, icon centred. */
.map-tools button { display: inline-flex; min-width: 36px; min-height: 34px; align-items: center; justify-content: center; padding: 6px; }
@media (max-width: 960px) {
  .workspace-tabs button { flex: 1; justify-content: center; }
  .section-head, .map-tools { align-items: stretch; flex-direction: column; }
}
</style>
